import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { skip, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import { OsintSearchService } from '../../../services/osint-search/osint-search.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { SearchHistoryService } from '../../../services/search-history/search-history.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { toPercent } from '../../../services/search-polling/osint-job-poll-response.util';
import { UtilsService } from '../../../services/utils/utils.service';
import { DocumentExportService } from '../../../services/document-export/document-export.service';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-osint-username-search',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    HeaderComponent,
    PaginatorComponent,
    SidebarComponent,
    RestrictedAccessWarningDirective, CreditsLabelPipe],
  templateUrl: './osint-username-search.component.html',
  styleUrl: './osint-username-search.component.css',
})
export class OsintUsernameSearchComponent implements OnInit, OnDestroy {
  accessDetails: any;
  activeTab: string = 'username';
  availableCredits: number | null = null;
  creditsLoading = true;
  totalCredits: number = 0;

  singleUsername: string = '';
  textAreaInput: string = '';
  multipleUsernames: string[] = [];
  extraUsernames: Array<{ value: string }> = [];
  readonly maxSearchRows = 5;
  isMultipleSearch: boolean = false;

  allJobResultsSubscription?: Subscription;
  loadingJobIds: boolean = false;
  jobIds: string[] = [];

  private lastSearchTime: number = 0;
  private readonly SEARCH_THROTTLE_MS: number = 1000; // 1 second

  searchType: string = 'username';
  loadingRecentResults: boolean = false;
  recentResults: any = [];
  recentResultsPaged: any = [];

  ipAddress: string = '';
  location: string = '';

  // Pagination State
  recentResultsCount: number = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage: boolean = false;
  recentResultsHasNextPage: boolean = false;
  recentResultsSelectAll = { isSelected: false };

  // Filtering State
  selectedFilterType: string = 'username';
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;
  filterTypes: any = [
    { label: 'Username', value: 'username' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];
  filterKey: string = '';
  filterValue: string = '';

  usernameFilter: string = '';
  notesFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }
    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );
    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((recentResultPaged: any) => recentResultPaged.isSelected)
      .map((recentResultPaged: any) => recentResultPaged.osint_search_id);
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private userAccountService: UserAccountService,
    private osintSearchService: OsintSearchService,
    private searchHistoryService: SearchHistoryService,
    private searchPollingService: SearchPollingService,
    private utilsService: UtilsService,
    private documentExportService: DocumentExportService,
    private searchServiceStatusService: SearchServiceStatusService,
  ) { }

  ngOnInit() {
    this.accessDetails = this.retrieveAccessDetails();

    // Check if OSINT service is disabled
    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = !searchServices.osint;
      }
    });

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'osintSearchCost',
      );
    });

    this.getRecentResults();
    this.getAvailableCredits();

    this.subscribeToJobResultsUpdates();

    // Check for auto-search from navigation
    const autoSearchState = history.state;
    if (autoSearchState?.autoSearch && autoSearchState?.searchQuery) {
      this.singleUsername = autoSearchState.searchQuery;
      // Clear the auto-search state to prevent re-triggering on back navigation
      history.replaceState({ ...history.state, autoSearch: false, searchQuery: null }, '');
      // Use setTimeout to ensure component is fully initialized
      setTimeout(() => {
        this.onSearchSingleUsernameButtonClick();
      }, 100);
    }
  }

  subscribeToJobResultsUpdates() {
    this.allJobResultsSubscription?.unsubscribe();

    this.allJobResultsSubscription = this.searchPollingService
      .getAllJobResults()
      .pipe(skip(1))
      .subscribe((map) => {
        this.recentResultsPaged.forEach((result: any) => {
          if (result.job_id) {
            const updatedStatus = map.get(result.job_id);
            if (updatedStatus) {
              this.applyJobPollStatusToRow(result, updatedStatus);
            }
          }
        });
      });
  }

  /** Map /osint/job poll fields onto a recent-history row for the status badge. */
  private applyJobPollStatusToRow(result: any, updatedStatus: any) {
    const deleted =
      updatedStatus.deleted === true || updatedStatus.status === 'deleted';
    const timedOut =
      updatedStatus.timed_out === true || updatedStatus.status === 'error';
    const progressPercent = toPercent(
      updatedStatus?.progress?.percent,
      updatedStatus.progress_percent,
    );
    const vendorSyncComplete = deleted || timedOut
      ? true
      : Boolean(updatedStatus.vendor_sync_complete);

    result.done = deleted || timedOut ? true : Boolean(updatedStatus.done);
    result.status = deleted
      ? 'deleted'
      : timedOut
        ? 'error'
        : vendorSyncComplete
          ? 'completed'
          : 'pending';
    result.deleted = deleted;
    result.timed_out = timedOut;
    result.progress_percent = progressPercent;
    result.progress_milestone = updatedStatus.progress_milestone ?? null;
    result.progress = updatedStatus.progress ?? result.progress ?? null;
    result.vendor_sync_complete = vendorSyncComplete;
    result.is_final = Boolean(
      vendorSyncComplete || deleted || timedOut,
    );
    result.vendor_status = updatedStatus.vendor_status ?? null;
    result.background_refresh_pending =
      deleted || timedOut ? false : !vendorSyncComplete;
  }

  /** Derive display status from history row (done + background_refresh_pending). */
  private deriveRowCompletionFlags(row: any) {
    if (
      row?.timed_out === true ||
      row?.status === 'error' ||
      row?.status === 'failed'
    ) {
      return {
        done: true,
        timed_out: true,
        background_refresh_pending: false,
        vendor_sync_complete: true,
        is_final: true,
        status: 'error',
      };
    }
    if (row?.deleted === true || row?.status === 'deleted') {
      return {
        done: true,
        deleted: true,
        background_refresh_pending: false,
        vendor_sync_complete: true,
        is_final: true,
        status: 'deleted',
      };
    }
    const done = Boolean(row?.done);
    // Treat missing/undefined pending as complete when done (older rows / final save)
    const refreshPending = row?.background_refresh_pending === true;
    const vendorSyncComplete = done && !refreshPending;
    return {
      done,
      background_refresh_pending: refreshPending,
      vendor_sync_complete: vendorSyncComplete,
      is_final: vendorSyncComplete,
      status: vendorSyncComplete ? 'completed' : 'pending',
    };
  }

  ngOnDestroy() {
    this.allJobResultsSubscription?.unsubscribe();
  }

  retrieveAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;
    return userPortal?.accessDetails || null;
  }

  private retrieveIPAndLocation(): { ip: string; location: string } {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    const ipRaw = userPortal?.ipDetails?.ip || '';
    const location = userPortal?.ipDetails?.city || '';

    const ip = ipRaw.trim();
    const isValidIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ip);
    console.log('🧪 Retrieved IP:', ip, 'Valid:', isValidIPv4);

    return {
      ip: isValidIPv4 ? ip : '',
      location,
    };
  }

  setTab(tab: string) {
    this.activeTab = tab;
    switch (tab) {
      case 'image':
        this.router.navigate(['/osint-image-search']);
        break;
      case 'number':
        this.router.navigate(['/osint-number-search']);
        break;
      case 'email':
        this.router.navigate(['/osint-email-search']);
        break;
      case 'username':
        this.router.navigate(['/osint-username-search']);
        break;
      case 'name':
        this.router.navigate(['/osint-name-search']);
        break;
      case 'wallet':
        this.router.navigate(['/osint-wallet-search']);
        break;
    }
  }

  goToSearchResultPage(searchResult: any) {
    this.router.navigate(
      [`/osint-username-search/search-result/${searchResult.job_id}`],
      {
        state: { searchResult },
      },
    );
  }

  isRowFullyComplete(row: any): boolean {
    if (!row) return false;
    if (
      row.status === 'error' ||
      row.status === 'failed' ||
      row.status === 'deleted' ||
      row.timed_out === true ||
      row.deleted === true
    ) {
      return true;
    }
    // Vendor must finish — do not treat result_ready / status "completed" alone as done.
    if (row.vendor_sync_complete === true) {
      return true;
    }
    if (row.vendor_sync_complete === false || row.background_refresh_pending === true) {
      return false;
    }
    if (row.is_final === true && row.background_refresh_pending !== true) {
      return true;
    }
    // History-only legacy: done and not still refreshing in background
    return Boolean(row.done) && row.background_refresh_pending !== true && row.status === 'completed';
  }

  isRowPolling(row: any): boolean {
    return !!row && !this.isRowFullyComplete(row);
  }

  isRowError(row: any): boolean {
    return (
      !!row &&
      (row.status === 'error' ||
        row.status === 'failed' ||
        row.timed_out === true)
    );
  }

  getRowStatusLabel(row: any): string {
    if (this.isRowError(row)) {
      return 'Error';
    }
    if (row?.deleted || row?.status === 'deleted') {
      return 'Deleted';
    }
    if (this.isRowFullyComplete(row)) {
      return 'Completed';
    }
    if (row?.done || row?.background_refresh_pending === true) {
      return 'Processing';
    }
    return 'Pending';
  }

  /** Display percent for table status badge — original 3rd-party progress.percent. */
  getRowProgressPercent(row: any): string {
    if (this.isRowError(row) || row?.deleted || row?.status === 'deleted') {
      return '';
    }
    const percent = this.resolveProgressPercent(row);
    return percent != null ? `${percent}%` : '…';
  }

  private resolveProgressPercent(row: any): number | null {
    if (!row) return null;
    return toPercent(
      row.progress?.percent,
      row.progress_percent,
      row.progress_milestone,
      row.progress?.milestone,
    );
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
        this.totalCredits = subscription?.total_credits;
      },
      error: (error) => {
        console.error(error);

        let errorMessage = 'Failed to load available credits';

        if (error?.error?.message_key) {
          errorMessage = error.error.message_key;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.statusText) {
          errorMessage = error.statusText;
        }

        this.toastr.error(errorMessage, 'Credits Load Failed');
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
    });
  }

  validateUsername(username: string) {
    const trimmedUsername = username.trim().replace(/\s+/g, '');
    const usernameRegex = /^[a-zA-Z0-9._-]{3,30}$/;

    if (!usernameRegex.test(trimmedUsername)) {
      return false;
    }
    return true;
  }

  prepareSearchValues() {
    return this.collectUsernameValues();
  }

  onSearchSingleUsernameButtonClick() {
    const values = this.collectUsernameValues();
    if (!values.length) {
      this.toastr.error('Please enter a username');
      return;
    }
    if (values.length > this.maxSearchRows) {
      this.toastr.error('You can only search a maximum of 5 usernames at once.');
      return;
    }
    if (!values.every((username) => this.validateUsername(username))) {
      this.toastr.error(
        values.length > 1
          ? 'One or more usernames are invalid.'
          : 'Username is invalid. Please check the format.',
      );
      return;
    }
    if (this.isThrottled()) {
      return;
    }
    this.isMultipleSearch = values.length > 1;
    this.multipleUsernames = values;
    this.getJobIds();
  }

  get canAddSearchRow(): boolean {
    return this.extraUsernames.length < this.maxSearchRows - 1;
  }

  addSearchRow(): void {
    if (!this.canAddSearchRow) {
      this.toastr.error('You can only search a maximum of 5 usernames at once.');
      return;
    }
    this.extraUsernames = [...this.extraUsernames, { value: '' }];
    this.isMultipleSearch = this.extraUsernames.length > 0;
  }

  removeSearchRow(index: number): void {
    this.extraUsernames = this.extraUsernames.filter((_, i) => i !== index);
    this.isMultipleSearch = this.extraUsernames.length > 0;
  }

  private collectUsernameValues(): string[] {
    const rawValues = this.isMultipleSearch
      ? this.textAreaInput.split('\n')
      : [this.singleUsername];
    return rawValues
      .map((value) => String(value || '').trim().replace(/\s+/g, ''))
      .filter((value) => value.length > 0);
  }

  extractMultipleUsernames() {
    this.multipleUsernames = this.textAreaInput
      .split('\n')
      .map((username) => username.trim().replace(/\s+/g, ''))
      .filter((username) => username.length > 0);
  }

  validateMultipleUsernames() {
    return this.multipleUsernames.every((username) =>
      this.validateUsername(username),
    );
  }

  onSearchMultipleUsernamesButtonClick() {
    this.extractMultipleUsernames();
    if (this.multipleUsernames.length > 5) {
      this.toastr.error(
        'You can only search a maximum of 5 usernames at once.',
      );
      return;
    }

    if (this.validateMultipleUsernames()) {
      if (this.isThrottled()) {
        return;
      }
      this.getJobIds();
    } else {
      this.toastr.error('One or more usernames are invalid.');
    }
  }

  private isThrottled(): boolean {
    const now = Date.now();
    const timeSinceLastSearch = now - this.lastSearchTime;

    return timeSinceLastSearch < this.SEARCH_THROTTLE_MS;
  }

  getJobIds() {
    const { ip, location } = this.retrieveIPAndLocation();
    //if (!ip) return;

    const searchValues = this.prepareSearchValues();
    this.loadingJobIds = true;
    this.lastSearchTime = Date.now();

    this.osintSearchService
      .getJobIds(this.searchType, searchValues, ip, location)
      .subscribe({
        next: (response) => {
          this.jobIds = this.extractJobIds(response?.data);

          // Root-level background poll — survives leaving this page until each job finishes
          this.searchPollingService.ensureBackgroundPolling(
            (this.jobIds || []).map((jobId: string, index: number) => ({
              job_id: jobId,
              searchType: this.searchType,
              label: String(searchValues?.[index] ?? jobId),
            })),
            3 * 1000,
          );
          this.getAllJobResults();

          this.resetSearchInputs();
          this.getRecentResults();
          this.loadingJobIds = false;
        },
        error: (error) => {
          console.error(error);
          this.loadingJobIds = false;

          let errorMessage = 'An error occurred while processing your request';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Search Failed');
        },
      });
  }

  onMultipleSearchChange(): void {
    if (this.isMultipleSearch) {
      if (!this.textAreaInput.trim() && this.singleUsername.trim()) {
        this.textAreaInput = this.singleUsername.trim();
      }
      return;
    }
    if (!this.singleUsername.trim()) {
      const first = this.textAreaInput
        .split('\n')
        .map((value) => value.trim())
        .find((value) => value.length > 0);
      if (first) {
        this.singleUsername = first;
      }
    }
  }

  resetSearchInputs() {
    this.singleUsername = '';
    this.textAreaInput = '';
    this.extraUsernames = [];
    this.multipleUsernames = [];
    this.isMultipleSearch = false;
  }

  extractJobIds(jobs: any[]) {
    if (!jobs || !Array.isArray(jobs)) {
      return [];
    }

    if (jobs.length > 0 && typeof jobs[0] === 'string') {
      return jobs;
    }

    return jobs.filter((job) => job.done === false).map((job) => job.job_id);
  }

  refreshPendingJobStatuses() {
    this.recentResultsPaged.forEach((result: any) => {
      if (result.job_id) {
        const cachedStatus = this.searchPollingService.getCachedJobStatus(
          result.job_id,
        );
        if (cachedStatus) {
          this.applyJobPollStatusToRow(result, cachedStatus);
        } else {
          Object.assign(result, this.deriveRowCompletionFlags(result));
        }
      }
    });

    // Re-attach any unfinished rows to the root background poller (survives page changes).
    const pendingRows = this.recentResultsPaged.filter((result: any) => {
      if (!result.job_id) return false;
      if (this.isRowFullyComplete(result)) return false;
      if (result.background_refresh_pending === true) return true;
      if (!result.done) return true;
      if (result.status === 'pending') return true;
      return false;
    });

    if (pendingRows.length > 0) {
      this.searchPollingService.ensureBackgroundPolling(
        pendingRows.map((result: any) => ({
          job_id: result.job_id,
          searchType: this.searchType,
          label: result.searched_username || result.job_id,
        })),
        3 * 1000,
      );
    }
  }

  getAllJobResults() { }

  getRecentResults() {
    this.loadingRecentResults = true;

    this.searchHistoryService
      .getOsintHistories(
        this.searchType,
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        this.filterKey,
        this.filterValue,
        this.startDateFilter,
        this.endDateFilter,
      )
      .subscribe({
        next: (response) => {
          this.loadingRecentResults = false;
          this.recentResults = response?.data?.data_list;
          this.recentResultsPaged = this.addAdditionalKeys(this.recentResults);

          this.recentResultsCount = response?.data?.total_items;
          this.recentResultsHasPreviousPage = response?.data?.has_previous;
          this.recentResultsHasNextPage = response?.data?.has_next;

          this.refreshPendingJobStatuses();
        },
        error: (error) => {
          console.error(error);
          this.loadingRecentResults = false;

          let errorMessage = 'Failed to load recent search results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Load Results Failed');
        },
      });
  }

  addAdditionalKeys(recentResults: any) {
    return (recentResults || []).map((recentResult: any) => ({
      ...recentResult,
      ...this.deriveRowCompletionFlags(recentResult),
      isSelected: false,
    }));
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.recentResultsSelectAll.isSelected = false;
    this.getRecentResults();
  }

  onRecentResultsSelectAllClick() {
    this.recentResultsSelectAll.isSelected =
      !this.recentResultsSelectAll.isSelected;

    this.recentResultsSelectAll.isSelected
      ? this.recentResultsPagedSelectAll()
      : this.recentResultsPagedDeselectAll();
  }

  recentResultsPagedSelectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: true,
      }),
    );
  }

  recentResultsPagedDeselectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: false,
      }),
    );
  }

  onRecentResultPagedClick(event: Event, recentResultPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    this.updateRecentResultPaged(isChecked, recentResultPaged.osint_search_id);
  }

  updateRecentResultPaged(isChecked: boolean, searchId: string) {
    const recentResultsPagedToUpdate = this.recentResultsPaged.find(
      (recentResultPaged: any) =>
        recentResultPaged.osint_search_id === searchId,
    );
    if (recentResultsPagedToUpdate) {
      recentResultsPagedToUpdate.isSelected = isChecked;
    }
  }

  onDeleteButtonClick() {
    if (this.selectedRecentResults.length === 0) {
      this.toastr.warning('Please select at least one result to delete.');
      return;
    }
    this.deleteRecentResults();
  }

  deleteRecentResults() {
    const deletedRows = this.recentResultsPaged.filter((row: any) =>
      this.selectedRecentResults.includes(row.osint_search_id),
    );
    const pollIdsToStop = deletedRows
      .flatMap((row: any) => [row.job_id, row.osint_search_id, row.portal_job_id])
      .filter(Boolean)
      .map((id: any) => String(id));

    this.searchHistoryService
      .deleteOsintHistories(this.selectedRecentResults)
      .subscribe({
        next: () => {
          if (pollIdsToStop.length) {
            this.searchPollingService.stopTrackingJobIds(pollIdsToStop);
          }
          this.toastr.success('Selected results deleted successfully.');
          this.recentResultsSelectAll.isSelected = false;
          this.updateRecentResultsPagedAfterDelete();
          this.updateRecentResultsCurrentPage();
          this.getRecentResults();
        },
        error: (error) => {
          console.error(error);

          let errorMessage = 'Failed to delete selected results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Delete Failed');
        },
      });
  }

  updateRecentResultsPagedAfterDelete() {
    this.recentResultsPaged = this.recentResultsPaged.filter(
      (recentResultPaged: any) =>
        !this.selectedRecentResults.includes(recentResultPaged.osint_search_id),
    );
  }

  updateRecentResultsCurrentPage() {
    if (this.recentResultsPaged.length === 0) {
      if (
        this.recentResultsHasPreviousPage &&
        this.recentResultsCurrentPage > 1
      ) {
        this.recentResultsCurrentPage--;
      }
    }
  }

  onFilterTypeChange() {
    this.resetFilters();
  }

  resetFilters() {
    this.recentResultsCurrentPage = 1;
    this.filterKey = '';
    this.filterValue = '';

    this.usernameFilter = '';
    this.notesFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';

    this.getRecentResults();
  }

  onUsernameFilterChange(username: string) {
    const trimmedUsername = username.trim();

    if (trimmedUsername === '') {
      this.filterKey = '';
      this.filterValue = '';
    } else if (trimmedUsername.length < 2) {
      return;
    } else {
      this.filterKey = 'searched_username';
      this.filterValue = trimmedUsername;
    }
    this.getRecentResults();
  }

  onNotesFilterChange(notes: string) {
    const trimmedNotes = notes.trim();

    if (trimmedNotes === '') {
      this.filterKey = '';
      this.filterValue = '';
    } else if (trimmedNotes.length < 2) {
      return;
    } else {
      this.filterKey = 'notes';
      this.filterValue = trimmedNotes;
    }
    this.getRecentResults();
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;
    this.applyDateFilter();
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;
    this.applyDateFilter();
  }

  applyDateFilter() {
    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';
    } else if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.filterValue = '';
    }
    if (this.startDateFilter && this.endDateFilter) {
      this.getRecentResults();
    }
  }

  onExportExcelButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToExcel(transformedRecentResultsPaged);
  }

  transformRecentResultsPaged() {
    return this.recentResultsPaged.map((recentResultPaged: any) => ({
      'OSINT SEARCH ID': recentResultPaged.osint_search_id,
      'JOB ID': recentResultPaged.job_id,
      'SEARCHED USERNAME': recentResultPaged.searched_username,
      'USER EMAIL': recentResultPaged.user?.email || '',
      'IP ADDRESS': recentResultPaged.ip_address,
      'LOCATION': recentResultPaged.location,
      'DEVICE': recentResultPaged.device,
      'NOTES':
        recentResultPaged.notes === undefined ? null : recentResultPaged.notes,
      'CREATED ON': this.utilsService.formatDateTime(
        recentResultPaged.created_on,
        'MMM d, y, h:mm:ss a',
      ),
    }));
  }

  exportToExcel(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `osint-username-results-${timestamp || 'export'}`;
    this.documentExportService.exportToExcel(
      transformedRecentResultsPaged,
      fileName,
      'OSINT Results',
    );
  }

  onExportCsvButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToCsv(transformedRecentResultsPaged);
  }

  exportToCsv(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `osint-username-results-${timestamp || 'export'}`;
    this.documentExportService.exportToCsv(
      transformedRecentResultsPaged,
      fileName,
      'OSINT Results',
    );
  }

  onExportPdfButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToPdf(transformedRecentResultsPaged);
  }

  exportToPdf(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `osint-username-results-${timestamp || 'export'}`;
    const title = `OSINT results generated from Ryno`;
    const username = this.getUsername();

    this.documentExportService.exportResultsToPdf(
      transformedRecentResultsPaged,
      fileName,
      title,
      username,
    );
  }

  private getUsername(): string {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.loginDetails?.user_name || '';
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  /** Truncate text to max words; if longer, append " ....." */
  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + ' .....';
  }
}
